import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  IconButton,
  Button,
  Tabs,
  Tab,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  NotificationsOutlined,
  CheckCircleOutline,
  DeleteOutline,
  SettingsOutlined,
  ChevronRight,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Notification } from '../services/userService';
import { getNotificationDestination } from '../utils/notificationNavigation';
import { PageContainer, PageHeader, EmptyState } from '../components/ui';
import { useUrlTab } from '../hooks/useUrlTab';
import { useShellTranslation } from '../components/navigation/useShellTranslation';
import { impersonationSubjectName } from '../components/navigation/notificationsView';

const TYPE_LABELS: Record<string, string> = {
  new_camera: 'New Product',
  map_pricing_update: 'MAP Pricing',
  system_update: 'System',
  user_role_changed: 'Account',
  account_suspended: 'Account',
  welcome: 'Welcome',
  organization_joined: 'Organization',
  map_violation: 'MAP Violation',
  product_added: 'New Product',
  product_updated: 'Product Update',
  price_changed: 'Price Change',
  integration_sync_completed: 'Sync',
  integration_sync_failed: 'Sync Failed',
  shopify_listing_hold: 'Listing match',
};

const formatNotificationDate = (date: any): string => {
  try {
    const d = date?.toDate?.() || new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

export const NotificationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useUrlTab(['all', 'unread'] as const, 'all');
  const {
    notifications,
    unreadNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
    userProfile,
  } = useAuth();
  const { getEffectiveRole, isImpersonating, impersonatedOrg, impersonatedBrand, impersonatedRole } =
    useImpersonation();
  const { t } = useShellTranslation();
  const navigate = useNavigate();

  const filteredNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return notifications.filter(n => !n.read);
    }
    return notifications;
  }, [notifications, activeTab]);

  // Resolve destinations as whoever the session currently is, matching
  // NotificationsMenu. Using the real profile while impersonating hands back
  // /app/admin routes, and AppShell treats those as leaving the workspace.
  const isAdmin = getEffectiveRole(userProfile) === 'admin';

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markNotificationAsRead(notification.id);
    }
    const destination = getNotificationDestination(notification, isAdmin);
    if (destination) {
      navigate(destination);
    }
  };

  // Same reason as the bell (NotificationsMenu): these are the admin's own
  // documents, not the impersonated account's, and the actions below would
  // clear the admin's real queue. Starting View-as does not navigate away, so
  // this page must hide them itself.
  if (isImpersonating) {
    return (
      <PageContainer maxWidth={900}>
        <PageHeader title="Notifications" />
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <EmptyState
            size="compact"
            icon={<NotificationsOutlined />}
            title={t('shell.notifications.hiddenWhileViewingAs', {
              who: impersonationSubjectName(
                { impersonatedOrg, impersonatedBrand, impersonatedRole },
                t,
              ),
            })}
          />
        </Paper>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth={900}>
      <PageHeader
        title="Notifications"
        description="Product, pricing, and account activity across your organization"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {unreadNotifications > 0 && (
              <Button
                size="small"
                variant="contained"
                onClick={markAllNotificationsAsRead}
                sx={{ textTransform: 'none' }}
              >
                Mark all as read
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<SettingsOutlined />}
              onClick={() => navigate('/app/profile')}
              sx={{ textTransform: 'none' }}
            >
              Preferences
            </Button>
          </Box>
        }
      />

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 44,
                minWidth: 'auto',
                px: 2,
              },
            }}
          >
            <Tab label={`All (${notifications.length})`} value="all" />
            <Tab label={`Unread (${unreadNotifications})`} value="unread" />
          </Tabs>
        </Box>

        {filteredNotifications.length === 0 ? (
          <EmptyState
            size="compact"
            icon={<NotificationsOutlined />}
            title={activeTab === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            description={
              activeTab === 'unread'
                ? 'All caught up!'
                : "You'll see notifications here when there's activity"
            }
          />
        ) : (
          <List disablePadding>
            {filteredNotifications.map((notification, index) => {
              const destination = getNotificationDestination(notification, isAdmin);
              return (
              <React.Fragment key={notification.id}>
                <ListItemButton
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    px: 3,
                    py: 2,
                    alignItems: 'flex-start',
                    cursor: destination ? 'pointer' : 'default',
                    '&:hover .notification-actions': { opacity: 1 },
                    '&:hover .notification-open': {
                      opacity: destination ? 1 : 0,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: notification.read ? 'transparent' : 'primary.main',
                      flexShrink: 0,
                      mt: 1,
                      mr: 2,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: notification.read ? 400 : 600 }}
                      >
                        {notification.title}
                      </Typography>
                      <Chip
                        label={TYPE_LABELS[notification.type] || notification.type}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {formatNotificationDate(notification.createdAt)}
                    </Typography>
                  </Box>
                  {destination && (
                    <ChevronRight
                      className="notification-open"
                      fontSize="small"
                      sx={{
                        color: 'text.disabled',
                        mt: 0.75,
                        opacity: { xs: 0.7, sm: 0.35 },
                        transition: 'opacity 0.15s',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box
                    className="notification-actions"
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      // Always visible on touch/mobile; hover reveals on larger screens
                      opacity: { xs: 1, sm: 0 },
                      transition: 'opacity 0.15s',
                      ml: 1,
                    }}
                  >
                    {!notification.read && (
                      <Tooltip title="Mark as read">
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            markNotificationAsRead(notification.id);
                          }}
                        >
                          <CheckCircleOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Dismiss">
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItemButton>
                {index < filteredNotifications.length - 1 && <Divider sx={{ ml: 3 }} />}
              </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>
    </PageContainer>
  );
};
